import React, { useState } from 'react';
import { Button, Checkbox, DatePicker, Modal, Space, Tooltip, message } from 'antd';
import { FileTextOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { escapeCsvField, downloadCsv } from '../utils/csv';
import { addressZipLocality } from '../utils/stopFilter';
import { getCollectionPointPassages, getMyCollectionPointPassages } from '../modules/collection-points/api';

const { RangePicker } = DatePicker;

// The calendar quarter before the current one (Q3 when today is in Q4).
function lastQuarter() {
  const prev = dayjs().subtract(3, 'month');
  const start = prev.startOf('month').subtract(prev.month() % 3, 'month');
  return [start, start.add(2, 'month').endOf('month')];
}

// #47 — CT billing: export every passage at every box over a period, with the
// box's NPA + commune in their own columns so the fiduciary aggregates per
// commune in Excel. One row per (order, box) — a multi-box stop yields one
// row per box, each with its own fill / weight.
export default function CollectionPointPassagesExport({ partnerId, viewOnly = false, partnerName }) {
  const [open, setOpen] = useState(false);
  const [range, setRange] = useState(() => [
    dayjs().subtract(1, 'month').startOf('month'),
    dayjs().subtract(1, 'month').endOf('month'),
  ]);
  const [completedOnly, setCompletedOnly] = useState(true);
  const [loading, setLoading] = useState(false);

  const runExport = async () => {
    if (!range?.[0] || !range?.[1]) return;
    const from = range[0].format('YYYY-MM-DD');
    const to = range[1].format('YYYY-MM-DD');
    setLoading(true);
    try {
      const { response } = viewOnly
        ? await getMyCollectionPointPassages(from, to)
        : await getCollectionPointPassages(partnerId, from, to);
      const rows = (response?.rows || []).filter((r) => !completedOnly || r.done);
      if (!rows.length) {
        message.info('No passage in this period');
        return;
      }
      const header = [
        'Date',
        'Box number',
        'Box',
        'Site',
        'Address',
        'NPA',
        'Commune',
        'Container',
        'Fill (/10)',
        'Weight (kg)',
        'Status',
        'Done',
        'Order',
        'Route',
        ...(viewOnly ? [] : ['Driver']),
      ];
      const lines = [
        header.map(escapeCsvField),
        ...rows.map((r) => {
          const z = addressZipLocality(r.siteAddress);
          return [
            dayjs(r.date).format('DD-MM-YYYY'),
            r.externalRef || '',
            r.boxName || '',
            r.siteName || '',
            r.siteAddress || '',
            z?.zip || '',
            r.commune || z?.locality || '',
            r.containerType || '',
            r.fillTenths ?? '',
            r.weight ?? '',
            r.status || '',
            r.done ? 'yes' : '',
            r.orderCode || '',
            r.routeId ?? '',
            ...(viewOnly ? [] : [r.driverName || '']),
          ].map(escapeCsvField);
        }),
      ];
      const slug = partnerName
        ? `${String(partnerName)
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')}-`
        : '';
      downloadCsv(lines, `${slug}passages-${from}_${to}.csv`);
      message.success(`${rows.length} passage(s) exported`);
      setOpen(false);
    } catch (e) {
      message.error(e?.message || 'Export failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Tooltip title="Export every passage (date, fill, weight, NPA, commune) over a period">
        <Button icon={<FileTextOutlined />} onClick={() => setOpen(true)} disabled={!viewOnly && !partnerId}>
          Passages
        </Button>
      </Tooltip>
      <Modal
        title="Export passages (CSV)"
        open={open}
        onCancel={() => setOpen(false)}
        onOk={runExport}
        okText="Export"
        confirmLoading={loading}
        okButtonProps={{ disabled: !range?.[0] || !range?.[1] }}
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>
            One line per box visited, by delivery date. The NPA and the commune are in their own columns so the file can
            be totalled per commune in Excel. A period may span up to 400 days.
          </div>
          <RangePicker
            value={range}
            onChange={setRange}
            format="DD.MM.YYYY"
            style={{ width: '100%' }}
            allowClear={false}
            presets={[
              {
                label: 'Last month',
                value: [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')],
              },
              { label: 'This month', value: [dayjs().startOf('month'), dayjs().endOf('month')] },
              { label: 'Last quarter', value: lastQuarter() },
              { label: 'This year', value: [dayjs().startOf('year'), dayjs().endOf('year')] },
            ]}
          />
          <Checkbox checked={completedOnly} onChange={(e) => setCompletedOnly(e.target.checked)}>
            Done passages only (box collected — untick to include skipped / failed / planned visits)
          </Checkbox>
        </Space>
      </Modal>
    </>
  );
}
